import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

function CandidateAttendance({ candidateId, token }) {
  const [location, setLocation] = useState({ lat: '', lng: '' });
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [todayCheckout, setTodayCheckout] = useState(null);
  // Debug: Track file input click
  const [fileInputClicked, setFileInputClicked] = useState(false);

  // Get today's attendance
  useEffect(() => {
    const authToken = token || localStorage.getItem('gogig_token');
    if (!authToken) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      return;
    }
    fetch(`${API_URL}/attendance/candidate/${candidateId}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(res => res.json())
      .then(data => {
        if (data.records) {
          const today = new Date();
          today.setHours(0,0,0,0);
          const checkin = data.records.find(r => r.type === 'checkin' && new Date(r.timestamp) >= today);
          const checkout = data.records.find(r => r.type === 'checkout' && new Date(r.timestamp) >= today);
          setTodayCheckin(checkin || null);
          setTodayCheckout(checkout || null);
        }
      });
  }, [candidateId, token, success]);

  // Get live location
  const getLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      err => setError('Location error: ' + err.message)
    );
  };

  // Handle image file
  const handleImage = e => {
    setImage(e.target.files[0]);
  };

  // Debug: Track file input click
  const handleFileInputClick = () => {
    setFileInputClicked(true);
    setError('');
    console.log('File input clicked');
  };

  // Check-in (Start Attendance)
  const handleCheckin = async () => {
    setError(''); setSuccess('');
    if (!location.lat || !location.lng) return setError('Location required');
    if (!image) return setError('Image required');
    setLoading(true);
    const form = new FormData();
    form.append('latitude', location.lat);
    form.append('longitude', location.lng);
    form.append('image', image);
    const authToken = token || localStorage.getItem('gogig_token');
    if (!authToken) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/attendance/checkin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: form
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Attendance started!');
        setImage(null);
      } else {
        setError(data.error || 'Check-in failed');
      }
    } catch (e) {
      setError('Check-in failed');
    }
    setLoading(false);
  };

  // Check-out (End Attendance)
  const handleCheckout = async () => {
    setError(''); setSuccess('');
    if (!location.lat || !location.lng) return setError('Location required');
    if (!image) return setError('Image required');
    setLoading(true);
    const form = new FormData();
    form.append('latitude', location.lat);
    form.append('longitude', location.lng);
    form.append('image', image);
    const authToken = token || localStorage.getItem('gogig_token');
    if (!authToken) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/attendance/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: form
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Attendance ended!');
        setImage(null);
      } else {
        setError(data.error || 'Check-out failed');
      }
    } catch (e) {
      setError('Check-out failed');
    }
    setLoading(false);
  };

  return (
    <>
      {/* Debug: Minimal file input for testing */}
      {/* Removed debug file input */}
      <div className="gogig-card" style={{ maxWidth: 400, margin: '0 auto', padding: 24 }}>
        <h2 style={{ marginBottom: 16 }}>Attendance</h2>
        <button className="gogig-card-btn" onClick={getLocation} style={{ marginBottom: 10 }}>Get Live Location</button>
        <div style={{ marginBottom: 10, color: '#555' }}>
          Location: {location.lat && location.lng ? `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}` : 'Not set'}
        </div>
        <label htmlFor="attendance-image" style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Upload Selfie for Attendance</label>
        <input
          id="attendance-image"
          type="file"
          accept="image/*"
          onClick={handleFileInputClick}
          onChange={handleImage}
          style={{ marginBottom: 10 }}
          disabled={loading}
        />
        {fileInputClicked && !image && (
          <div style={{ color: '#f87171', fontSize: 13, marginBottom: 6 }}>
            File input was clicked, but no file was selected. If the file picker does not open, there may be a browser or overlay issue.
          </div>
        )}
        {image && (
          <div style={{ marginBottom: 8, fontSize: 14, color: '#333' }}>
            Selected: {image.name}
            <button
              type="button"
              onClick={() => setImage(null)}
              style={{ marginLeft: 10, color: '#fff', background: '#f87171', border: 'none', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontSize: 13 }}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        )}
        {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
        {success && <div style={{ color: 'green', marginBottom: 8 }}>{success}</div>}
        {/* Attendance buttons logic */}
        {!todayCheckin && (
          <button className="gogig-card-btn" onClick={handleCheckin} disabled={loading}>Start Attendance</button>
        )}
        {todayCheckin && !todayCheckout && (
          <button className="gogig-card-btn" onClick={handleCheckout} disabled={loading}>End Attendance</button>
        )}
        {todayCheckin && todayCheckout && (
          <div style={{ color: '#22c55e', marginTop: 10, fontWeight: 600 }}>Attendance completed for today!</div>
        )}
        <h4 style={{ marginTop: 18 }}>Today's Attendance</h4>
        <ul style={{ maxHeight: 180, overflowY: 'auto', padding: 0 }}>
          {todayCheckin && (
            <li style={{ marginBottom: 8, listStyle: 'none', borderBottom: '1px solid #eee', paddingBottom: 4 }}>
              <b>Check-in</b> at {new Date(todayCheckin.timestamp).toLocaleTimeString()}<br />
              <span style={{ fontSize: 13, color: '#888' }}>{todayCheckin.latitude.toFixed(5)}, {todayCheckin.longitude.toFixed(5)}</span><br />
              <img src={todayCheckin.imageUrl} alt="proof" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, marginTop: 4 }} />
            </li>
          )}
          {todayCheckout && (
            <li style={{ marginBottom: 8, listStyle: 'none', borderBottom: '1px solid #eee', paddingBottom: 4 }}>
              <b>Check-out</b> at {new Date(todayCheckout.timestamp).toLocaleTimeString()}<br />
              <span style={{ fontSize: 13, color: '#888' }}>{todayCheckout.latitude.toFixed(5)}, {todayCheckout.longitude.toFixed(5)}</span><br />
              <img src={todayCheckout.imageUrl} alt="proof" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, marginTop: 4 }} />
            </li>
          )}
        </ul>
      </div>
    </>
  );
}

export default CandidateAttendance; 